import { useMemo } from 'react';

import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';

import { isAutoIdSettings } from 'api/meta/enums/nodes/properties/EPropertySettingUtils';
import { BlueprintIcon } from 'components/icons/blueprint/BlueprintIcon';
import type { PropertyValue } from 'state/data/dataState';
import { useSelectedProjectTemplateNode } from 'state/selectedProject/selectedProjectHooks';

interface NodeDetailsHeaderIdProps {
  templateNodeId: string;
  /** Property values sourced from the useNodeDetail query (not GRID_STORE). */
  properties: Record<string, PropertyValue>;
}

/**
 * Decoupled variant of the grid's NodeId chip: the auto-id value comes from the
 * useNodeDetail query record passed in, rather than from GRID_STORE.
 */
export function NodeDetailsHeaderId({ templateNodeId, properties }: NodeDetailsHeaderIdProps) {
  const targetTemplateNode = useSelectedProjectTemplateNode(templateNodeId);

  const { icon, label } = useMemo(() => {
    const nodeName =
      targetTemplateNode?.displayNames.find((d) => d.languageCode === 'en')?.value ??
      targetTemplateNode?.technicalName ??
      '';

    const autoIdProperty = targetTemplateNode?.properties.find((p) => isAutoIdSettings(p.settings));
    const autoIdValue =
      autoIdProperty !== undefined ? (properties[autoIdProperty.technicalName] ?? nodeName) : nodeName;

    const nodeIcon = targetTemplateNode?.icon ?? 'cube';

    return { icon: nodeIcon, label: autoIdValue.toString() };
  }, [targetTemplateNode, properties]);

  return (
    <Stack direction='row' alignItems='center' flex='1 0 auto' gap={0.5}>
      <Chip
        label={label}
        size='small'
        icon={<BlueprintIcon name={icon} fontSize='small' />}
        sx={{
          backgroundColor: 'common.white',
          color: 'text.secondary'
        }}
      />
    </Stack>
  );
}
